import { useEffect, useRef } from 'react';
import { useInView, useMotionValue, useSpring } from 'motion/react';

interface AnimatedCounterProps {
  target: number;
  duration?: number;
  decimals?: number;
  prefix?: string;
  suffix?: string;
  className?: string;
  /**
   * Separadores de miles: `20298` → `20.298`.
   *
   * Opcional y apagado por defecto porque no todo número los quiere: un año
   * (2026) o un código quedarían como `2.026`. Se enciende para importes.
   */
  locale?: boolean;
}

export function AnimatedCounter({
  target,
  duration = 1.5,
  decimals = 0,
  prefix = '',
  suffix = '',
  className,
  locale = false,
}: AnimatedCounterProps) {
  const ref = useRef<HTMLSpanElement>(null);
  const isInView = useInView(ref, { once: true });
  const motionValue = useMotionValue(0);
  const springValue = useSpring(motionValue, {
    duration: duration * 1000,
    bounce: 0,
  });

  useEffect(() => {
    if (isInView) {
      motionValue.set(target);
    }
  }, [isInView, target, motionValue]);

  useEffect(() => {
    const render = (n: number) => {
      const cuerpo = locale
        ? n.toLocaleString('es-BO', {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
          })
        : n.toFixed(decimals);
      if (ref.current) ref.current.textContent = prefix + cuerpo + suffix;
    };

    /*
      El valor se dibuja UNA VEZ antes de suscribirse.

      El resorte solo avisa cuando cambia, así que un contador cuyo objetivo es
      **cero** nunca recibía un cambio y el `<span>` quedaba vacío: la tarjeta
      mostraba su título y su explicación, sin número. Pasaba justo en el caso
      que más se ve —"0 por vencer", "0 pendientes"— y se leía como si el dato
      no hubiera cargado.
    */
    render(springValue.get());

    return springValue.on('change', render);
  }, [springValue, decimals, prefix, suffix, locale]);

  return <span ref={ref} className={className} />;
}
